import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Download, Heart, MoreHorizontal, RefreshCw } from 'lucide-react';
import ImageSpaceRenameModal from './ImageSpaceRenameModal';
import {
  fetchImageCardMeta,
  renameImageCard,
  toggleImageCardFavorite,
} from '@/pages/ipe/workbench/utils/imageCardMetaActions';
import { showToast } from '@/utils/toast';
import '@/styles/ipe/workbench/components/ImageCardMoreAction.css';

const MENU_OVERLAP_PX = 2;
const VIEWPORT_GAP = 8;
const CLOSE_DELAY_MS = 220;
const MENU_EST_WIDTH = 140;
const MENU_EST_HEIGHT = 120;
const MENU_EST_HEIGHT_WITH_REMAKE = 156;

/**
 * 图片卡片底部「更多」：悬停展开 下载 / 收藏 / 重命名。
 * 重命名弹窗不展示后缀（复用 ImageSpaceRenameModal）。
 */
export default function ImageCardMoreAction({
  disabled = false,
  imageId = '',
  imageUrl = '',
  imageName = '',
  favorited: favoritedProp = false,
  navId = '',
  taskId = '',
  outputIndex = null,
  candidateIds = [],
  onDownload,
  onFavorite,
  onRename,
  onRemake,
  className = 'sourcing-product-card__action',
  menuAlign = 'right',
}) {
  const { t } = useTranslation('workbench');
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [favorited, setFavorited] = useState(Boolean(favoritedProp));
  const [resolvedName, setResolvedName] = useState(String(imageName || ''));
  const [resolvedRecordId, setResolvedRecordId] = useState(String(imageId || '').trim());
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameSaving, setRenameSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFavorited(Boolean(favoritedProp));
  }, [favoritedProp]);

  useEffect(() => {
    setResolvedName(String(imageName || ''));
  }, [imageName]);

  useEffect(() => {
    setResolvedRecordId(String(imageId || '').trim());
  }, [imageId]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setMenuOpen(false);
      setMenuPosition(null);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    clearCloseTimer();
    setMenuOpen(true);
  }, [clearCloseTimer, disabled]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const triggerRect = trigger.getBoundingClientRect();
    const panel = menuRef.current;
    const width = panel?.offsetWidth || MENU_EST_WIDTH;
    const height = panel?.offsetHeight
      || (typeof onRemake === 'function' ? MENU_EST_HEIGHT_WITH_REMAKE : MENU_EST_HEIGHT);

    // 与触发按钮轻微重叠，避免移入菜单时穿过空隙触发 leave 闪烁
    let top = triggerRect.top - height + MENU_OVERLAP_PX;
    if (top < VIEWPORT_GAP) {
      top = triggerRect.bottom - MENU_OVERLAP_PX;
    }
    let left = menuAlign === 'left'
      ? triggerRect.left
      : triggerRect.right - width;
    left = Math.max(
      VIEWPORT_GAP,
      Math.min(left, window.innerWidth - width - VIEWPORT_GAP),
    );
    top = Math.max(
      VIEWPORT_GAP,
      Math.min(top, window.innerHeight - height - VIEWPORT_GAP),
    );

    setMenuPosition((prev) => {
      if (prev && prev.top === top && prev.left === left) return prev;
      return { top, left };
    });
  }, [menuAlign, onRemake]);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    updateMenuPosition();
    const onLayout = () => updateMenuPosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [menuOpen, updateMenuPosition]);

  const resolveOptions = useCallback(() => ({
    url: String(imageUrl || '').trim(),
    navId: String(navId || '').trim(),
    taskId: String(taskId || '').trim(),
    outputIndex,
    candidateIds: Array.isArray(candidateIds) ? candidateIds : [],
  }), [candidateIds, imageUrl, navId, outputIndex, taskId]);

  const ensureMetaForRename = useCallback(async () => {
    const id = String(imageId || '').trim();
    const options = resolveOptions();
    if (!id && !options.url && !options.taskId) {
      return { id: '', name: resolvedName, favorited };
    }
    // 始终按 id / url / taskId 解析真实入库记录，避免详情页展示名覆盖空间内名称
    const meta = await fetchImageCardMeta(id, options);
    if (meta?.id) {
      setResolvedRecordId(meta.id);
      if (meta.name) setResolvedName(meta.name);
      setFavorited(Boolean(meta.favorited));
      return meta;
    }
    return { id, name: resolvedName, favorited };
  }, [favorited, imageId, resolveOptions, resolvedName]);

  const handleDownload = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuOpen(false);
    setMenuPosition(null);
    if (disabled || typeof onDownload !== 'function') return;
    onDownload();
  }, [disabled, onDownload]);

  const handleFavorite = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || busy) return;
    setBusy(true);
    try {
      if (typeof onFavorite === 'function') {
        const next = await onFavorite(!favorited);
        if (typeof next === 'boolean') setFavorited(next);
        setMenuOpen(false);
        setMenuPosition(null);
        return;
      }
      const next = await toggleImageCardFavorite(imageId, favorited, resolveOptions());
      if (typeof next === 'boolean') setFavorited(next);
      setMenuOpen(false);
      setMenuPosition(null);
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, favorited, imageId, onFavorite, resolveOptions]);

  const handleRenameClick = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || busy) return;
    const options = resolveOptions();
    if (!String(imageId || '').trim() && !options.url && !options.taskId && typeof onRename !== 'function') {
      showToast(t('studioToast.imageNotStored'), 'warning');
      setMenuOpen(false);
      setMenuPosition(null);
      return;
    }
    setBusy(true);
    try {
      const meta = await ensureMetaForRename();
      if (!meta?.id && typeof onRename !== 'function') {
        showToast(t('studioToast.imageNotStored'), 'warning');
        setMenuOpen(false);
        setMenuPosition(null);
        return;
      }
      if (meta?.name) setResolvedName(meta.name);
      setMenuOpen(false);
      setMenuPosition(null);
      setRenameOpen(true);
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, ensureMetaForRename, imageId, onRename, resolveOptions, t]);

  const handleRenameConfirm = useCallback(async (id, nextFullName) => {
    setRenameSaving(true);
    const options = resolveOptions();
    try {
      // 确认前再 resolve：避免占位 id「image」或历史错误重建 id
      const meta = await fetchImageCardMeta(
        String(resolvedRecordId || id || imageId || '').trim(),
        options,
      );
      if (!meta?.id) {
        showToast(t('studioToast.imageNotStored'), 'warning');
        return;
      }
      setResolvedRecordId(meta.id);
      if (typeof onRename === 'function') {
        const result = await onRename(meta.id, nextFullName);
        // 约定：返回 { ok: false } 视为失败；未返回 / void / true / { ok: true } 视为成功
        if (result && typeof result === 'object' && result.ok === false) {
          return;
        }
        const nextId = result && typeof result === 'object' ? result.id : '';
        const nextName = result && typeof result === 'object'
          ? (result.name || nextFullName)
          : nextFullName;
        if (nextId) setResolvedRecordId(String(nextId));
        setResolvedName(nextName);
        setRenameOpen(false);
        return;
      }
      const result = await renameImageCard(meta.id, nextFullName, options);
      if (result?.ok) {
        if (result.id) setResolvedRecordId(String(result.id));
        setResolvedName(result.name || nextFullName);
        setRenameOpen(false);
      }
    } catch {
      // toast 由 rename / onRename 侧负责
    } finally {
      setRenameSaving(false);
    }
  }, [imageId, onRename, resolveOptions, resolvedRecordId, t]);

  const handleRemakeClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || busy || typeof onRemake !== 'function') return;
    setMenuOpen(false);
    setMenuPosition(null);
    onRemake();
  }, [busy, disabled, onRemake]);

  const menuPanel = menuOpen
    ? createPortal(
      <div
        ref={menuRef}
        className="image-card-more-action__menu"
        style={menuPosition
          ? { top: menuPosition.top, left: menuPosition.left }
          : { top: -9999, left: -9999 }}
        role="menu"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          role="menuitem"
          disabled={disabled || typeof onDownload !== 'function'}
          onClick={handleDownload}
        >
          <Download size={14} aria-hidden="true" />
          {t('studioChrome.download')}
        </button>
        <button
          type="button"
          role="menuitem"
          disabled={disabled || busy}
          onClick={handleFavorite}
        >
          <Heart size={14} aria-hidden="true" fill={favorited ? 'currentColor' : 'none'} />
          {favorited ? t('studioChrome.unfavorite') : t('studioChrome.favorite')}
        </button>
        <button
          type="button"
          role="menuitem"
          disabled={disabled || busy}
          onClick={handleRenameClick}
        >
          {t('studioChrome.rename')}
        </button>
        {typeof onRemake === 'function' ? (
          <button
            type="button"
            role="menuitem"
            disabled={disabled || busy}
            onClick={handleRemakeClick}
          >
            <RefreshCw size={14} aria-hidden="true" />
            {t('studioChrome.remake')}
          </button>
        ) : null}
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`image-card-more-action ${className}${menuOpen ? ' is-open is-active' : ''}`}
        disabled={disabled}
        aria-label={t('studioChrome.more')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (menuOpen) {
            scheduleClose();
            return;
          }
          openMenu();
        }}
      >
        <MoreHorizontal size={14} aria-hidden="true" />
        {t('studioChrome.more')}
      </button>
      {menuPanel}
      <ImageSpaceRenameModal
        open={renameOpen}
        image={{
          id: String(resolvedRecordId || imageId || '').trim() || 'image',
          name: resolvedName || t('studioChrome.unnamedImage'),
        }}
        saving={renameSaving}
        onClose={() => {
          if (!renameSaving) setRenameOpen(false);
        }}
        onConfirm={handleRenameConfirm}
      />
    </>
  );
}
